import React from 'react';
import { Loader } from 'freemium-ui';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  NUMBER_OF_ALLOWED_CONDITIONS,
  WorkFlowMatchOptions,
} from 'routes/screens/wflow/constants';
import {
  ConditionGroupContainer,
  Group,
  IntOperator,
} from 'routes/screens/wflow/styles';
import { WorkFlowMatchOptionType } from 'routes/screens/wflow/types';
import { ActionButton } from 'routes/styles';
import Icon from 'shared/components/Icon';
import SwitchButton from 'shared/components/SwitchButton';
import { Center } from 'shared/styles';
import {
  SurveyType,
  WorkflowConditionGroupType,
  WorkflowConditionOptions,
  WorkflowType,
} from 'shared/types';
import Condition from './condition';

type ConditionGroupProps = {
  id?: string;
  disabled: boolean;
  conditionGroupIndex: number;
  conditionGroup: WorkflowConditionGroupType;
  conditionGroupLength: number;
  surveyData?: SurveyType;
  isFetchingSurvey?: boolean;
};

const ConditionGroup = (props: ConditionGroupProps) => {
  const {
    disabled,
    conditionGroup,
    conditionGroupIndex,
    conditionGroupLength,
    surveyData,
    isFetchingSurvey,
  } = props;
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<WorkflowType>();

  const conditions_operator = watch(
    `condition_groups.${conditionGroupIndex}.conditions_operator`,
  );
  const condition_groups = watch('condition_groups');
  const conditions = watch(
    `condition_groups.${conditionGroupIndex}.conditions`,
  );

  const onAddCondition = () => {
    const newConditions = [...(conditions || [])];
    newConditions.push({});
    setValue(
      `condition_groups.${conditionGroupIndex}.conditions`,
      newConditions,
    );
  };

  const onDeleteCondition = (indexToDelete) => {
    const newConditions = [...(conditions || [])].filter(
      (cg, idx) => idx !== indexToDelete,
    );
    setValue(
      `condition_groups.${conditionGroupIndex}.conditions`,
      newConditions,
    );
  };

  const onDeleteConditionGroup = () => {
    const newConditionGroups = [...(condition_groups || [])].filter(
      (cg, idx) => idx !== conditionGroupIndex,
    );
    setValue('condition_groups', newConditionGroups);
  };

  const workFlowMatchOptions = React.useMemo(() => {
    return Object.keys(WorkFlowMatchOptions).map((key) => ({
      id: key as WorkflowConditionOptions,
      label: t(WorkFlowMatchOptions[key]),
    }));
  }, []);

  if (isFetchingSurvey)
    return (
      <ConditionGroupContainer>
        <Center>
          <Loader />
        </Center>
      </ConditionGroupContainer>
    );

  return (
    <ConditionGroupContainer>
      <ActionButton
        className={disabled || conditionGroupLength === 1 ? 'disabled' : ''}
        onClick={() => {
          if (disabled || conditionGroupLength === 1) return;
          onDeleteConditionGroup();
        }}
      >
        <div>
          <Icon width="12" height="12" glyph="minus" />
        </div>
      </ActionButton>
      <Group>
        <div className="wf-row-condition">
          <Trans
            i18nKey="workflow.condition.condition"
            components={{
              switchButton: (
                <SwitchButton
                  value={conditions_operator || ''}
                  disabled={disabled}
                  onChange={(item: WorkFlowMatchOptionType) =>
                    setValue(
                      `condition_groups.${conditionGroupIndex}.conditions_operator`,
                      item.id,
                    )
                  }
                  items={workFlowMatchOptions}
                  itemLabelIdentifier="label"
                  itemValueIdentifier="id"
                />
              ),
            }}
          />
        </div>
        {conditionGroup.conditions?.map((condition, idx) => (
          <>
            <div className="wf-cg-container">
              <ActionButton
                className={`wf-c-delete ${
                  disabled || conditionGroup.conditions?.length === 1
                    ? 'disabled'
                    : ''
                }`.trim()}
                onClick={() => {
                  if (disabled || conditionGroup.conditions?.length === 1)
                    return;
                  onDeleteCondition(idx);
                }}
              >
                <div>
                  <Icon width="12" height="12" glyph="minus" />
                </div>
              </ActionButton>
              <Condition
                key={condition.id}
                conditionGroupIndex={conditionGroupIndex}
                conditionIndex={idx}
                disabled={disabled}
                surveyData={surveyData}
                isFetchingSurvey={isFetchingSurvey}
              />
            </div>
            {idx < (conditionGroup?.conditions || []).length - 1 &&
              conditionGroup.conditions_operator && (
                <IntOperator>
                  {t(conditionGroup.conditions_operator?.toLowerCase())}
                </IntOperator>
              )}
          </>
        ))}
        <ActionButton
          onClick={onAddCondition}
          className={
            disabled ||
            (conditionGroup.conditions &&
              conditionGroup.conditions?.length >= NUMBER_OF_ALLOWED_CONDITIONS)
              ? 'disabled'
              : ''
          }
        >
          <div>
            <Icon width="12" height="12" glyph="plus" />
          </div>
          <div>{`${t('workflow.condition.new_condition')}`}</div>
        </ActionButton>
      </Group>
    </ConditionGroupContainer>
  );
};

export default ConditionGroup;
